import { createStorageValue } from '@interhyp/platform-storage';
import { createValue } from '@interhyp/platform-value';
import { z } from 'zod';
import { mortgageIntentNamespace } from '../namespaces';
/* In the case of construction projects, the following values must be defined:
 * the construction costs
 * the price of the land
 */
const buildSchema = z.object({
    reason: z.literal('Bau'),
    costsBuilding: z.number(),
    priceLand: z.number(),
});
/* In case of purchase of a new building, the following values must be defined:
 * the purchase price
 */
const buyNewSchema = z.object({
    reason: z.literal('KaufNeu'),
    priceBuilding: z.number(),
});
/* In case of purchase of an existing property, the following values must be defined:
 * the purchase price
 * the equity capital
 * optional renovation/modernization costs
 */
const buyExistingSchema = z.object({
    reason: z.literal('KaufBest'),
    priceBuilding: z.number(),
    renovationCosts: z.number().optional(),
});
/* In case of follow-up financing, the following values must be defined,
 * the market value
 * the payout date (after the existing debit interest commitment ends)
 * the remaining debt to be financed (loan amount)
 *
 * Already existing loans on the property (preload) can be defined optionally
 */
const followUpSchema = z.object({
    reason: z.literal('Umschuld'),
    marketValue: z.number(),
    payoutDate: z.coerce.date(),
    residualDebt: z.number(),
    additionalPriorityLoan: z.number().optional(),
});
/* In the case of a modernization, reconstruction or renovation, the following values must be defined
 * the market value,
 * the amount needed for the modernization/reconstruction (loan amount)
 *
 * Already existing loans on the property (preload) can be defined optionally
 */
const rebuildSchema = z.object({
    reason: z.literal('Umbau'),
    marketValue: z.number(),
    desiredLoanAmount: z.number(),
    additionalPriorityLoan: z.number().optional(),
});
/* In the case of raising capital, the following values must be defined
 * the market value,
 * the amount needed (loan amount)
 *
 * Already existing loans on the property (preload) can be defined optionally
 */
const capitalSchema = z.object({
    reason: z.literal('KapitalB'),
    marketValue: z.number(),
    desiredLoanAmount: z.number(),
    additionalPriorityLoan: z.number().optional(),
});
/* In the case of a consumer credit, the following values must be defined
 * the amount needed (loan amount)
 */
const consumerCreditSchema = z.object({
    reason: z.literal('KonsKred'),
    desiredLoanAmount: z.number(),
});
/* The venture object consists of one of the reason and its respective values and
 * the fixed period wanted (maturity) (optional)
 */
const ventureSchema = z
    .discriminatedUnion('reason', [
    buyNewSchema,
    buyExistingSchema,
    followUpSchema,
    buildSchema,
    rebuildSchema,
    capitalSchema,
    consumerCreditSchema,
])
    .and(z.object({
    fixedPeriodWanted: z.number().optional(),
}));
/* The venture object needs to be overwritten as total to prevent that inconsistent states are saved
 * with values that do not belong to the same venture.
 */
export const ventureStorageValue = createStorageValue({
    namespace: mortgageIntentNamespace,
    id: Symbol('venture'),
    schema: ventureSchema,
    type: 'local',
});
export const ventureMemoryValue = createValue({
    namespace: mortgageIntentNamespace,
    id: Symbol('ventureMemory'),
    schema: ventureSchema,
});
